import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, MoreHorizontal } from "lucide-react";

/** Something done to the whole item from its "More" menu (History, Delete). */
export interface EditorHeadAction { label: string; onSelect: () => void; tone?: "danger"; disabled?: boolean }

/**
 * The head of an item's editor: a way back, the title and its status, what applies to the whole item (`aside`, the
 * availability switch) and a "More" menu for History and Delete. With room, all in one row. Narrower (a tablet, or the
 * chat open beside it), back and More take the first row and the title shares the second with the switch, without its
 * hint. Narrowest, the switch becomes a full-width card, hint and all, under the title.
 */
export function EditorHead({ backLabel, backDisabled, onBack, title, status, aside, menu }: { backLabel: string; backDisabled?: boolean; onBack: () => void; title: string; status?: ReactNode; aside?: ReactNode; menu?: EditorHeadAction[] }) {
  // The frame is a size container: the head lays itself out by the room the editor has, not the screen's width.
  return <div className="bw-area-editor-frame"><header className="bw-area-editor-head">
    <button type="button" className="bw-btn bw-btn--text bw-area-back" disabled={backDisabled} onClick={onBack}><ChevronLeft size={16} aria-hidden="true" />{backLabel}</button>
    <div className="bw-area-editor-title"><h1>{title}</h1>{status}</div>
    {aside && <div className="bw-area-editor-aside">{aside}</div>}
    {menu?.length ? <MoreMenu label={`More for ${title}`} items={menu} /> : null}
  </header></div>;
}

function MoreMenu({ label, items }: { label: string; items: EditorHeadAction[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => { if (!wrap.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", away);
    wrap.current?.querySelector<HTMLButtonElement>("[role=menuitem]:not(:disabled)")?.focus();
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  return <div className="bw-area-more" ref={wrap} onKeyDown={(event) => {
    if (event.key === "Escape" && open) { event.stopPropagation(); setOpen(false); wrap.current?.querySelector<HTMLButtonElement>(".bw-area-more-button")?.focus(); }
    if (open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      const buttons = [...(wrap.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]:not(:disabled)") ?? [])];
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(at + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length]?.focus();
    }
  }}>
    <button type="button" className="bw-btn bw-btn--icon bw-area-more-button" aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(!open)}><MoreHorizontal size={20} aria-hidden="true" /></button>
    {open && <div id={id} className="bw-area-more-menu" role="menu" aria-label={label}>
      {items.map((item) => <button key={item.label} type="button" role="menuitem" data-tone={item.tone} disabled={item.disabled} onClick={() => { setOpen(false); item.onSelect(); }}>{item.label}</button>)}
    </div>}
  </div>;
}
